import { Form, useSearchParams, useSubmit } from '@remix-run/react'
import { useRef } from 'react'
import { useDebouncedCallback } from 'use-debounce'

export type FilterOption = { id: string; title?: string | null }

interface FilterFieldsetProps {
  title: string
  name: string
  options: FilterOption[]
  selectedItems: string[]
  onChange: () => void
}

const FilterFieldset = ({ name, options, selectedItems, title, onChange }: FilterFieldsetProps) => (
  <fieldset className="rounded-3xl border-2 border-dark p-4 dark:border-white">
    <legend className="ml-3 px-2 font-bold">{title}</legend>
    <ul className="space-y-2">
      {options.map(option => (
        <li key={option.id} className="hover:text-baseOrange dark:hover:text-lightOrange">
          <label htmlFor={option.id} className="flex items-center space-x-2 leading-snug">
            <input
              type="checkbox"
              id={option.id}
              name={name}
              value={option.id}
              defaultChecked={selectedItems.includes(option.id)}
              className="block"
              onChange={onChange}
            />
            <span>{option.title}</span>
          </label>
        </li>
      ))}
    </ul>
  </fieldset>
)

interface FiltersProps {
  sets?: FilterOption[]
  tags?: FilterOption[]
  learningAreas?: FilterOption[]
  type: 'printable' | 'video'
}

const Filters = ({ sets, learningAreas, tags }: FiltersProps) => {
  const ref = useRef<HTMLFormElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [params] = useSearchParams()
  const submit = useSubmit()

  const selectedAreas = params.getAll('area')
  const selectedSets = params.getAll('set')
  const selectedTags = params.getAll('tag')
  // const search = params.get('q');

  const onSubmit = useDebouncedCallback(() => {
    if (ref.current) {
      const form = new FormData(ref.current)

      if (!form.get('q')) {
        form.delete('q')
      }

      submit(form, { method: 'get', replace: true })
    }
  }, 500)

  const onClear = () => {
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    submit(new FormData(), { method: 'get', replace: true })
  }

  return (
    <Form className="max-w-sm space-y-6" ref={ref} onReset={onClear}>
      {learningAreas?.length ? (
        <FilterFieldset
          options={learningAreas}
          selectedItems={selectedAreas}
          title="Learning Area"
          name="area"
          onChange={onSubmit}
        />
      ) : null}
      {sets?.length ? (
        <FilterFieldset
          options={sets}
          selectedItems={selectedSets}
          title="Set"
          name="set"
          onChange={() => {
            if (ref.current) {
              ref.current.submit()
            }
          }}
        />
      ) : null}
      {tags?.length ? (
        <FilterFieldset
          options={tags}
          selectedItems={selectedTags}
          title="Tag"
          name="tag"
          onChange={() => {
            if (ref.current) {
              ref.current.submit()
            }
          }}
        />
      ) : null}
      <div className="flex justify-center">
        <button type="reset" className="px-3 py-1 font-bold hover:underline">
          Clear filters
        </button>
      </div>
    </Form>
  )
}

export default Filters
